<script lang="ts">
	/**
	 * `/Settings/Admin/Settings/Email` — who sends the store's mail, what it says, and what is waiting.
	 *
	 * Email is the one part of the store that fails **silently and lawfully-badly**: with no sender
	 * configured, checkout still succeeds, entitlements are still correct, and the customer simply
	 * never receives the receipt for money they have already paid (Design §11). Nothing on the
	 * storefront changes, so the only place that can say so is this screen — which is why the
	 * unreceipted-sales banner is persistent and sits above everything else rather than being a
	 * number in a metrics panel.
	 *
	 * The three panels are the three questions an operator has: who sends (Sender), what it says
	 * (Templates), and what has not gone out (Queue).
	 */

	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Modal
	} from 'flowbite-svelte';
	import {
		EmailProviderForm,
		EmailTemplateEditor,
		EmptyState,
		InfiniteLoadElement,
		PageHeader,
		Panel,
		RelativeDate,
		SelectField
	} from '../../components';
	import {
		createEmailProvider,
		getOperationsHealth,
		listEmailMessages,
		listEmailProviderTypes,
		listEmailProviders,
		listEmailTemplates,
		makeEmailProviderDefault,
		retryEmailMessage,
		sendEmailProviderTest,
		updateEmailProvider,
		verifyEmailProvider
	} from '../../AppNetwork';
	import EmailMessageViewer from './email/EmailMessageViewer.svelte';
	import type {
		EmailMessage,
		EmailProviderConfig,
		EmailProviderWriteInput,
		EmailTemplateSummary,
		OperationsHealth,
		EmailProviderTypeDescriptor
	} from '../../AppTypes';
	import { toastError, toastSuccess, toastWarning } from '../../stores/toast';

	const PAGE_SIZE = 25;

	/** The message open in the viewer, or empty. */
	let viewingUUID: string = '';

	let providers: EmailProviderConfig[] = [];
	let types: EmailProviderTypeDescriptor[] = [];
	let templates: EmailTemplateSummary[] = [];
	let health: OperationsHealth | null = null;

	let loading: boolean = true;
	let loadError: string = '';

	/** The row being edited, `null` for the create form, `undefined` for "no form open". */
	let editing: EmailProviderConfig | null | undefined = undefined;
	let saving: boolean = false;
	let verifyingUUID: string = '';
	let promotingUUID: string = '';
	let testingUUID: string = '';

	let selectedTemplate: EmailTemplateSummary | null = null;

	let messages: EmailMessage[] = [];
	let messageTotal: number = 0;
	let messagesLoading: boolean = true;
	let messagesError: string = '';
	let statusFilter: string = '';
	let retryingUUID: string = '';
	let sentinel: InfiniteLoadElement | undefined;

	const HEALTH_TONE: Record<string, 'green' | 'red' | 'yellow'> = {
		healthy: 'green',
		unhealthy: 'red',
		undecryptable: 'red',
		unknown: 'yellow'
	};

	/** Health is stated in words as well as colour, here and inside the form. */
	const HEALTH_LABEL: Record<string, string> = {
		healthy: 'Verified',
		unhealthy: 'Verification failed',
		undecryptable: 'Cannot decrypt',
		unknown: 'Not verified'
	};

	const STATUS_TONE: Record<string, 'green' | 'red' | 'yellow' | 'dark'> = {
		pending: 'dark',
		sent: 'green',
		failed: 'red',
		expired: 'yellow'
	};

	const STATUS_LABEL: Record<string, string> = {
		pending: 'Queued',
		sent: 'Sent',
		failed: 'Failed',
		expired: 'Expired'
	};

	const statusOptions = [
		{ value: '', name: 'Any status' },
		{ value: 'pending', name: 'Queued' },
		{ value: 'sent', name: 'Sent' },
		{ value: 'failed', name: 'Failed' },
		{ value: 'expired', name: 'Expired' }
	];

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			const [providerResponse, typeResponse, templateResponse] = await Promise.all([
				listEmailProviders(),
				listEmailProviderTypes(),
				listEmailTemplates()
			]);
			providers = providerResponse.items;
			types = typeResponse.items;
			templates = templateResponse.items;
			if (selectedTemplate !== null) {
				selectedTemplate =
					templates.find(
						candidate =>
							candidate.key === selectedTemplate?.key &&
							candidate.locale === selectedTemplate?.locale
					) ?? null;
			}
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/**
	 * Health is loaded on its own and its failure is swallowed: the banner is important, but an
	 * operations endpoint that is briefly unavailable must not take the sender configuration —
	 * the screen that fixes the problem — down with it.
	 */
	async function fnLoadHealth(): Promise<void> {
		try {
			health = await getOperationsHealth();
		} catch {
			health = null;
		}
	}

	function fnReplace(updated: EmailProviderConfig): void {
		providers = providers.map(provider => (provider.uuid === updated.uuid ? updated : provider));
		if (editing && editing.uuid === updated.uuid) {
			editing = updated;
		}
	}

	async function fnSave(event: CustomEvent<{ input: EmailProviderWriteInput }>): Promise<void> {
		saving = true;
		try {
			if (editing === null) {
				const created = await createEmailProvider(event.detail.input);
				providers = [...providers, created];
				editing = created;
				toastSuccess(
					`${created.name} created.`,
					'Send a test message before making it the default sender.'
				);
			} else if (editing) {
				const updated = await updateEmailProvider(editing.uuid, event.detail.input);
				fnReplace(updated);
				toastSuccess(`${updated.name} saved.`);
			}
			await fnLoadHealth();
		} catch (error) {
			toastError('The sender was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnVerify(provider: EmailProviderConfig): Promise<void> {
		verifyingUUID = provider.uuid;
		try {
			const checked = await verifyEmailProvider(provider.uuid);
			fnReplace(checked);
			if (checked.healthStatus === 'healthy') {
				toastSuccess(`${checked.name} verified.`);
			} else {
				// A failure here is the point of the button, not an error in the request.
				toastWarning(`${checked.name} did not verify.`, checked.healthError ?? '');
			}
		} catch (error) {
			toastError('The verification could not be run.', (error as Error).message);
		} finally {
			verifyingUUID = '';
		}
	}

	async function fnMakeDefault(provider: EmailProviderConfig): Promise<void> {
		promotingUUID = provider.uuid;
		try {
			await makeEmailProviderDefault(provider.uuid);
			// Promoting clears another row too, so the whole list is re-read rather than patched.
			await fnLoad();
			await fnLoadHealth();
			toastSuccess(`${provider.name} now sends the store's mail.`, 'Queued messages will drain.');
		} catch (error) {
			toastError('The default sender was not changed.', (error as Error).message);
		} finally {
			promotingUUID = '';
		}
	}

	async function fnSendTest(provider: EmailProviderConfig, to: string): Promise<void> {
		testingUUID = provider.uuid;
		try {
			const result = await sendEmailProviderTest(provider.uuid, to);
			if (result.delivered) {
				toastSuccess(`Test message sent to ${result.to}.`, result.message);
			} else {
				toastWarning('The test message was not delivered.', result.message);
			}
		} catch (error) {
			toastError('The test message could not be sent.', (error as Error).message);
		} finally {
			testingUUID = '';
		}
	}

	function fnTemplateChanged(event: CustomEvent<{ template: EmailTemplateSummary }>): void {
		const updated = event.detail.template;
		templates = templates.map(candidate =>
			candidate.key === updated.key && candidate.locale === updated.locale ? updated : candidate
		);
		selectedTemplate = updated;
	}

	async function fnLoadMessages(reset: boolean = true): Promise<void> {
		if (reset) {
			messagesLoading = true;
			messages = [];
		}
		messagesError = '';
		try {
			const page = await listEmailMessages({
				status: statusFilter,
				limit: PAGE_SIZE,
				offset: reset ? 0 : messages.length
			});
			messages = reset ? page.items : [...messages, ...page.items];
			messageTotal = page.total;
		} catch (error) {
			messagesError = (error as Error).message;
		} finally {
			messagesLoading = false;
		}
	}

	async function fnLoadMoreMessages(): Promise<void> {
		await fnLoadMessages(false);
		sentinel?.resetVisibilityTracking();
		sentinel?.updateState();
	}

	async function fnRetry(message: EmailMessage): Promise<void> {
		retryingUUID = message.uuid;
		try {
			await retryEmailMessage(message.uuid);
			toastSuccess('Queued for the next send.');
			await fnLoadMessages();
			await fnLoadHealth();
		} catch (error) {
			toastError('The message was not queued.', (error as Error).message);
		} finally {
			retryingUUID = '';
		}
	}

	$: hasDefaultSender = providers.some(provider => provider.isDefault && provider.isActive);
	$: hasMoreMessages = messages.length < messageTotal;
	$: unreceiptedSales = health?.email?.unreceiptedSales === true;

	onMount(() => {
		fnLoad();
		fnLoadHealth();
		fnLoadMessages();
	});
</script>

{#if unreceiptedSales}
	<!--
		Persistent and first on the page, by design. Money has been taken and the customer has no
		receipt: that is a consumer-protection failure, not a cosmetic one, and it does not clear
		itself. Nothing dismisses this — it disappears when the receipts have somewhere to go.
	-->
	<div
		class="widget mb-6 border-2 border-red-500 bg-red-50 dark:bg-red-950/40"
		role="alert"
		data-testid="unreceipted-sales-banner"
	>
		<div class="flex items-start gap-3">
			<i class="fa-solid fa-triangle-exclamation fa-xl text-red-600 dark:text-red-400 mt-1" />
			<div class="min-w-0">
				<p class="font-semibold text-red-700 dark:text-red-300">
					This store has taken payments it has not emailed a receipt for.
				</p>
				<p class="text-sm text-default mt-1">
					No email sender is configured, so customers who have paid have received nothing: no
					receipt, no confirmation, and no record of what they bought. The messages are queued
					rather than lost: configure a sender below and the store will send them. Some of them
					expire, so this is worth doing today rather than this week.
				</p>
			</div>
		</div>
	</div>
{/if}

<PageHeader title="Email">
	<svelte:fragment slot="description">
		The sender the store's mail goes out through, the copy it sends, and everything still waiting
		to be delivered.
	</svelte:fragment>
	<svelte:fragment slot="help">
		Credentials are encrypted at rest and are never returned by any endpoint, so reads show a mask.
		With no sender configured, messages are queued rather than dropped, so nothing is lost by
		configuring one late; but a message whose wording stops being true is discarded when its
		expiry passes.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<EmptyState variant="loading" title="Loading the email configuration…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The email configuration could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<div class="space-y-4">
		<Panel title="Sender" faIcon="paper-plane">
			<svelte:fragment slot="titleRight">
				<Button
					size="sm"
					color="primary"
					disabled={types.length === 0}
					on:click={() => (editing = null)}
				>
					<i class="fa-solid fa-plus mr-2" />Add sender
				</Button>
			</svelte:fragment>

			{#if types.length === 0}
				<EmptyState
					title="This build registers no email adapters."
					description="A sender can only be created for an adapter the running build knows about."
				/>
			{:else if providers.length === 0}
				<EmptyState
					faIcon="envelope"
					title="No email provider is configured."
					description="Nothing is lost while this is true: receipts, welcome messages and payment warnings are queued rather than dropped, and they are sent as soon as a sender exists. But nothing reaches a customer until then, and a message whose wording expires is discarded instead."
				>
					<Button color="primary" size="sm" on:click={() => (editing = null)}>
						Configure the first sender
					</Button>
				</EmptyState>
			{:else}
				<div class="space-y-2">
					{#if !hasDefaultSender}
						<p class="text-sm text-red-600 dark:text-red-500">
							No sender is both active and default, so the store still queues everything it wants
							to send. Promote one below.
						</p>
					{/if}

					{#each providers as provider (provider.uuid)}
						<div class="widget bg-depth depth-hover flex flex-wrap items-center gap-3">
							<div class="min-w-0 grow">
								<div class="flex flex-wrap items-center gap-2">
									<span class="font-semibold truncate">{provider.name}</span>
									{#if provider.isDefault}
										<Badge color="green">Default sender</Badge>
									{/if}
									{#if !provider.isActive}
										<Badge color="dark">Inactive</Badge>
									{/if}
									<Badge color={HEALTH_TONE[provider.healthStatus] ?? 'yellow'}>
										{HEALTH_LABEL[provider.healthStatus] ?? provider.healthStatus}
									</Badge>
								</div>
								<p class="text-xs text-default mt-1">
									{provider.type}
									{#if provider.fromAddress}
										· from {provider.fromAddress}
									{/if}
									{#if provider.healthCheckedAt}
										· checked <RelativeDate date={provider.healthCheckedAt} size="xs" />
									{/if}
								</p>
								{#if provider.healthError}
									<p class="text-xs text-red-600 dark:text-red-500 mt-1 break-words">
										{provider.healthError}
									</p>
								{/if}
							</div>

							<Button size="xs" color="alternative" on:click={() => (editing = provider)}>
								<i class="fa-solid fa-pen mr-1" />Edit
							</Button>
						</div>
					{/each}
				</div>
			{/if}
		</Panel>

		<!-- Wrapped rather than rebuilt: the form owns its own fields and actions. -->
		<Modal
			open={editing !== undefined}
			size="lg"
			outsideclose={!saving}
			dismissable={!saving}
			title=""
			on:close={() => {
				if (!saving) editing = undefined;
			}}
		>
			{#if editing !== undefined}
			<EmailProviderForm
				provider={editing}
				{types}
				{saving}
				verifying={verifyingUUID === editing?.uuid}
				promoting={promotingUUID === editing?.uuid}
				testing={testingUUID === editing?.uuid}
				on:save={fnSave}
				on:verify={() => editing && fnVerify(editing)}
				on:makeDefault={() => editing && fnMakeDefault(editing)}
				on:sendTest={event => editing && fnSendTest(editing, event.detail.to)}
				on:cancel={() => (editing = undefined)}
			/>
			{/if}
		</Modal>

		<Panel title="Templates" faIcon="file-lines">
			<svelte:fragment slot="titleRight">
				<span class="text-default text-xs">
					{templates.filter(template => template.isOverridden).length} customised of
					{templates.length}
				</span>
			</svelte:fragment>

			<p class="text-sm text-default mb-3">
				Every template ships with the build, so none of them can be missing, only customised. A
				customised template stops following later corrections to the shipped copy, which is why
				resetting one is offered as plainly as editing it.
			</p>

			{#if templates.length === 0}
				<EmptyState
					title="This build ships no email templates."
					description="Nothing can be sent until a build that defines them is deployed."
				/>
			{:else}
				<div class="space-y-2">
					{#each templates as template (`${template.key}/${template.locale}`)}
						{@const isSelected =
							selectedTemplate !== null &&
							selectedTemplate.key === template.key &&
							selectedTemplate.locale === template.locale}
						<button
							type="button"
							class="widget bg-depth depth-hover w-full text-left flex flex-wrap items-center gap-3 {isSelected
								? 'border-widget'
								: ''}"
							on:click={() => (selectedTemplate = isSelected ? null : template)}
						>
							<div class="min-w-0 grow">
								<div class="flex flex-wrap items-center gap-2">
									<span class="font-semibold truncate">{template.key}</span>
									<Badge color="blue">{template.locale}</Badge>
									{#if template.isOverridden}
										<Badge color="purple">Customised</Badge>
									{:else}
										<Badge color="dark">Shipped default</Badge>
									{/if}
								</div>
								<p class="text-xs text-default mt-1 truncate">{template.subject}</p>
							</div>
							<i class="fa-solid {isSelected ? 'fa-angle-up' : 'fa-angle-down'} text-default" />
						</button>
					{/each}
				</div>
			{/if}
		</Panel>

		<!-- The template editor is large; `xl` keeps the preview readable inside the modal. -->
		<Modal
			open={selectedTemplate !== null}
			size="xl"
			title=""
			on:close={() => (selectedTemplate = null)}
		>
			{#if selectedTemplate !== null}
				<EmailTemplateEditor
					template={selectedTemplate}
					on:changed={fnTemplateChanged}
					on:close={() => (selectedTemplate = null)}
				/>
			{/if}
		</Modal>

		<Panel title="Queue" faIcon="inbox">
			<svelte:fragment slot="titleRight">
				<span class="text-default text-xs">
					{messageTotal}
					{messageTotal === 1 ? 'message' : 'messages'}
				</span>
			</svelte:fragment>

			<div class="space-y-4">
				<p class="text-sm text-default">
					Everything the store has wanted to send. A queued message is not a lost one; it waits
					for a working sender. Retrying an expired message sends it anyway, so check its wording
					is still true before you do.
				</p>

				<div class="controlpanel flex flex-wrap items-center gap-3">
					<SelectField
						items={statusOptions}
						bind:value={statusFilter}
						on:change={() => fnLoadMessages()}
						placeholder="Status"
						size="sm"
						class="w-44"
					/>
					<Button size="sm" color="alternative" on:click={() => fnLoadMessages()}>
						<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
					</Button>
				</div>

				{#if messagesLoading}
					<EmptyState variant="loading" title="Loading the queue…" />
				{:else if messagesError.length > 0}
					<EmptyState variant="error" title="The queue could not be loaded." description={messagesError}>
						<Button color="alternative" size="sm" on:click={() => fnLoadMessages()}>Try again</Button>
					</EmptyState>
				{:else if messages.length === 0}
					<EmptyState
						title="Nothing is waiting."
						description="No message matches this filter. An empty queue with a working sender means everything the store wanted to send has gone out."
					/>
				{:else}
					<Table divClass="relative overflow-x-auto">
						<TableHead>
							<TableHeadCell>To</TableHeadCell>
							<TableHeadCell>Template</TableHeadCell>
							<TableHeadCell>Subject</TableHeadCell>
							<TableHeadCell>Status</TableHeadCell>
							<TableHeadCell>Attempts</TableHeadCell>
							<TableHeadCell>Queued</TableHeadCell>
							<TableHeadCell />
						</TableHead>
						<TableBody>
							{#each messages as message (message.uuid)}
								<TableBodyRow>
									<TableBodyCell class="dataTable break-all">{message.to}</TableBodyCell>
									<TableBodyCell class="dataTable">
										<div>{message.templateKey}</div>
										{#if message.locale}
											<div class="text-default text-xs">{message.locale}</div>
										{/if}
									</TableBodyCell>
									<TableBodyCell class="dataTable">
										<button
											type="button"
											class="text-left text-primary-600 dark:text-primary-400 hover:underline"
											on:click={() => (viewingUUID = message.uuid)}
										>
											{message.subject}
										</button>
									</TableBodyCell>
									<TableBodyCell class="dataTable">
										<!-- Words, not only a colour. -->
										<Badge color={STATUS_TONE[message.status] ?? 'dark'}>
											{STATUS_LABEL[message.status] ?? message.status}
										</Badge>
										{#if message.lastError}
											<div class="text-default text-xs break-all mt-1">{message.lastError}</div>
										{/if}
									</TableBodyCell>
									<TableBodyCell class="dataTable">{message.attemptCount}</TableBodyCell>
									<TableBodyCell class="dataTable">
										{#if message.sentAt}
											<RelativeDate date={message.sentAt} />
										{:else}
											<RelativeDate date={message.creationDate} />
										{/if}
									</TableBodyCell>
									<TableBodyCell class="dataTable">
										{#if message.status !== 'sent'}
											<Button
												size="xs"
												color="alternative"
												disabled={retryingUUID === message.uuid}
												on:click={() => fnRetry(message)}
											>
												Retry now
											</Button>
										{/if}
									</TableBodyCell>
								</TableBodyRow>
							{/each}
						</TableBody>
					</Table>

					{#if hasMoreMessages}
						<!-- The sentinel fires on the false→true edge only, so it is reset after each page. -->
						<InfiniteLoadElement bind:this={sentinel} on:becomeVisible={fnLoadMoreMessages} />
					{/if}
				{/if}
			</div>
		</Panel>
	</div>
{/if}

<EmailMessageViewer messageUUID={viewingUUID} on:close={() => (viewingUUID = '')} />
